import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { dashboardViewEntryPoint } from '@jira/global-pages__dashboard-view/src/dashboardViewEntryPoint.entrypoint.tsx';
import { dashboardsAnalyticsEntryPoint } from '@jira/global-pages__dashboards-analytics/src/dashboardsAnalyticsEntryPoint.entrypoint.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { expVal } from '@atlassian/jira-feature-experiments';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type {
	DashboardLastVisitedEntryPoints,
	DashboardLastVisitedQueries,
} from './DashboardLastVisitedRoot.tsx';
import parameters from './__generated__/DashboardLastVisitedRootQuery$parameters';

type DashboardLastVisitedRootComponent = EntryPointComponent<
	DashboardLastVisitedQueries,
	DashboardLastVisitedEntryPoints,
	{},
	{}
>;

/**
 * Preloads the classic dashboard always, and while dual create is on, the AVP
 * dashboard plus the query that decides between them. Classic dashboard is
 * always preloaded to avoid waiting for a round trip.
 */
export const dashboardLastVisitedEntryPoint: EntryPoint<
	DashboardLastVisitedRootComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<DashboardLastVisitedRootComponent>(
		/* webpackChunkName: "dashboard-last-visited-entrypoint" */ './DashboardLastVisitedRoot.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: (params: EntryPointRouteParams) => {
		const classic = { entryPoint: dashboardViewEntryPoint, entryPointParams: params };

		if (!expVal('platform_avp_jd_merged_directory_afm', 'isEnabled', false)) {
			return { entryPoints: { classic } };
		}

		const { cloudId, activationId } = params.tenantContext;

		return {
			queries: {
				lastVisitedQueryRef: {
					parameters,
					variables: {
						cloudId,
						workspaceAri: JiraWorkspaceAri.create({
							siteId: cloudId,
							activationId,
						}).toString(),
					},
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
				},
			},
			entryPoints: {
				classic,
				avp: { entryPoint: dashboardsAnalyticsEntryPoint, entryPointParams: params },
			},
		};
	},
});
